import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Field, TextInput } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const wrap = (children) => (
  <div className="flex min-h-screen items-center justify-center bg-brand-black px-5">
    <div className="w-full max-w-sm space-y-8">
      <div className="flex justify-center">
        <Link to="/" aria-label="A2Z Rankings home"><Logo /></Link>
      </div>
      {children}
      <p className="text-center text-xs text-brand-mist">
        <Link to="/" className="transition-colors hover:text-brand-emerald">Back to website</Link>
      </p>
    </div>
  </div>
);

const btnCls =
  "inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black transition-all duration-200 hover:bg-brand-snow disabled:opacity-60";

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/me").then(() => navigate("/admin", { replace: true })).catch(() => {});
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await api.post("/auth/login", form);
      navigate("/admin", { replace: true });
    } catch (err) {
      setState("error");
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return wrap(
    <form onSubmit={submit} className="surface-card space-y-6 p-8" data-testid="admin-login-form">
      <div className="space-y-1 text-center">
        <h1 className="font-display text-xl font-bold text-brand-snow">Admin sign in</h1>
        <p className="text-xs text-brand-mist">Authorised A2Z Rankings administrators only.</p>
      </div>
      <Field label="Email" htmlFor="admin-email" required>
        <TextInput id="admin-email" type="email" testid="admin-login-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="username" required />
      </Field>
      <Field label="Password" htmlFor="admin-password" required>
        <TextInput id="admin-password" type="password" testid="admin-login-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" required />
      </Field>
      {state === "error" && (
        <p role="alert" data-testid="admin-login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>
      )}
      <button type="submit" disabled={state === "sending"} data-testid="admin-login-submit" className={btnCls}>
        {state === "sending" ? <><Loader2 size={15} className="animate-spin" /> Signing in…</> : "Sign in"}
      </button>
      <p className="text-center text-xs">
        <Link to="/admin/forgot" data-testid="admin-forgot-link" className="text-brand-mist transition-colors hover:text-brand-emerald">
          Forgot your password?
        </Link>
      </p>
    </form>
  );
};

export const AdminForgot = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {}
    setSending(false);
    setDone(true);
  };

  return wrap(
    <div className="surface-card space-y-6 p-8" data-testid="admin-forgot-form">
      <div className="space-y-1 text-center">
        <h1 className="font-display text-xl font-bold text-brand-snow">Reset password</h1>
        <p className="text-xs text-brand-mist">We'll email a one-hour reset link to the admin address.</p>
      </div>
      {done ? (
        <p className="rounded-lg border border-brand-emerald/30 bg-brand-emerald/10 px-4 py-3 text-sm text-brand-emerald" data-testid="admin-forgot-done">
          If that email is registered, a reset link is on its way.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <Field label="Admin email" htmlFor="forgot-email" required>
            <TextInput id="forgot-email" type="email" testid="admin-forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
          <button type="submit" disabled={sending} data-testid="admin-forgot-submit" className={btnCls}>
            {sending ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
      <p className="text-center text-xs">
        <Link to="/admin/login" className="text-brand-mist transition-colors hover:text-brand-emerald">Back to sign in</Link>
      </p>
    </div>
  );
};

export const AdminReset = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setState("done");
      setTimeout(() => navigate("/admin/login"), 1500);
    } catch (err) {
      setState("error");
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return wrap(
    <div className="surface-card space-y-6 p-8" data-testid="admin-reset-form">
      <h1 className="text-center font-display text-xl font-bold text-brand-snow">Choose a new password</h1>
      {state === "done" ? (
        <p className="rounded-lg border border-brand-emerald/30 bg-brand-emerald/10 px-4 py-3 text-sm text-brand-emerald" data-testid="admin-reset-done">
          Password updated. Redirecting to sign in…
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <Field label="New password" htmlFor="reset-password" required hint="Minimum 10 characters.">
            <TextInput id="reset-password" type="password" testid="admin-reset-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={10} required />
          </Field>
          {state === "error" && (
            <p role="alert" data-testid="admin-reset-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>
          )}
          <button type="submit" disabled={state === "sending" || !token} data-testid="admin-reset-submit" className={btnCls}>
            {state === "sending" ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </div>
  );
};
